/**
 * KAN-834: шапка карточки клиента в приложении: имя, организация и город,
 * телефон с кнопкой "Позвонить", теплота, этап, следующий контакт.
 * KAN-851: теплота значком кабинета, название организации ведёт в её карточку.
 * KAN-852: "Чей клиент" (ничей / ваш / у ФИО) вместо строки "Менеджер",
 * пометка следующего контакта.
 * KAN-856: под "Позвонить" Telegram клиента и наш аккаунт переписки (MobileClientTelegram).
 * KAN-857: Telegram-аватар клиента слева от имени.
 */
import type { ReactNode } from 'react';
import { ClientAvatar } from '@/components/clients/ClientAvatar';
import { ManagerCallButton } from '@/components/clients/ManagerCallButton';
import { formatAttentionDate } from '@/components/dashboard/attentionFormat';
import { useOrgTimezone } from '@/hooks/useOrgTimezone';
import { LeadTemperatureBadge } from '@/components/leads/LeadTemperatureBadge';
import { MobileClientTelegram } from '@/components/mobile/MobileClientTelegram';
import { MobileCompanyLink } from '@/components/mobile/MobileCompanyLink';
import { MobileOwnerBadge } from '@/components/mobile/MobileOwnerBadge';
import { useClientOwnerLabel } from '@/hooks/useMobileWork';
import { isLeadTemperatureLevel } from '@/lib/leadTemperature';
import { clientTitle } from '@/lib/mobileClient';
import type { Client } from '@/types/client';

function Fact({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex justify-between gap-3 py-1.5 text-sm">
      <dt className="text-gray-500">{label}</dt>
      <dd className="text-right text-gray-900">{value}</dd>
    </div>
  );
}

export function MobileClientSummary({ client }: { client: Client }): JSX.Element {
  const city = client.client_company?.city || '';
  // KAN-840: следующий контакт по времени организации, а не браузера.
  const timeZone = useOrgTimezone();
  // KAN-852: ничей / ваш / у ФИО.
  const ownerLabel = useClientOwnerLabel(client.manager);
  return (
    <section className="rounded-xl border border-gray-200 bg-white p-4" data-testid="mobile-client-summary">
      <div className="flex items-center gap-3">
        <ClientAvatar avatarUrl={client.avatar_url} name={clientTitle(client)} size={48} />
        <h1 className="min-w-0 break-words text-xl font-semibold text-gray-900">{clientTitle(client)}</h1>
      </div>
      {(client.client_company || client.company) && (
        <p className="text-sm text-gray-600" data-testid="mobile-client-place">
          <MobileCompanyLink company={client.client_company} fallback={client.company} />
          {city && <span>, {city}</span>}
        </p>
      )}
      {client.phone && (
        <div className="mt-3">
          <ManagerCallButton clientId={client.id} phone={client.phone} size="lg" testId="mobile-client-call" targetMode="eager" />
        </div>
      )}
      <div className="mt-3">
        <MobileClientTelegram client={client} />
      </div>
      <dl className="mt-3 divide-y divide-gray-100">
        {isLeadTemperatureLevel(client.lead_temperature) && (
          <Fact label="Теплота" value={<LeadTemperatureBadge level={client.lead_temperature} />} />
        )}
        {client.stage?.label && <Fact label="Этап" value={client.stage.label} />}
        <Fact
          label="Следующий контакт"
          value={client.followup_at ? formatAttentionDate(client.followup_at, timeZone) : 'не назначен'}
        />
        <Fact
          label="Чей клиент"
          value={<MobileOwnerBadge label={ownerLabel} testId="mobile-client-owner" />}
        />
        {client.followup_note && <Fact label="Что сделать" value={client.followup_note} />}
      </dl>
    </section>
  );
}
